import React, {
  createContext, useEffect, useReducer,
} from 'react';

import { AppActionTypes, setSuperFrameLoaded, setError } from 'context/actions';
import { appReducer } from 'context/reducers';
import { ErrorType } from 'services/server';

import { CreativeType, DisplayBannerT, PlayerSizesType, CreativeGalleryParams, CreativeViewType } from 'types/creative'; 

import config from 'config';
import { loadExternalResources } from '../services/utils';

// we can expand this or create separate contexts
// need to import a type/interface when we decide what init state properties will be
export type AppContextType = {
  loading?: boolean,
  error?: ErrorType | null,
  creative?: CreativeType,
  mobileView?: boolean,
  webView?: boolean,
  superFrameLoaded?: boolean,
  screenSizeClass?: string,
  playerSizeFormat: string,
  displayBanner?: DisplayBannerT,
  mainContent?: HTMLDivElement,
  showBannerOptions?: boolean, 
  galleryParams?: CreativeGalleryParams,
  activeCreativeView: CreativeViewType, // creative size to be displayed.
  sizeViewList?: any[],
};

const initialState: AppContextType = {
  loading: false,
  error: null,
  creative: {} as CreativeType,
  mobileView: false,
  webView: false,
  superFrameLoaded: false,
  screenSizeClass: '',
  playerSizeFormat: '2', // default size
  displayBanner: {
    active: false,
    show: false,
    size: {
      width: 300,
      height: 250,
    }
  },
  showBannerOptions:false,
  galleryParams: {},
  activeCreativeView: {
    width: 800,
    height: 450,
    fullscreen: false,
    loaded: false,
    share: false,
  },
  sizeViewList:[]
};

// The data type of the action object.
export type DispatchType = {
  type: AppActionTypes;
  payload?: any; // TODO: stricter type once we iron out what we need to dispatch
};

export type ShareUrlParamsType = {
  mobileView: boolean,
  playerSizeFormat: string,
  displayBanner: {
    active: boolean,
    size: {
      width: number,
      height: number,
    }
  },
};

// separate state and dispatch context (isolate state changes and only what a component needs)
export const AppContext = createContext<AppContextType | {}>({});

// we need to generalize the dispatch creative action type
export const AppDispatch = createContext<React.Dispatch<DispatchType> | undefined>(undefined);

// Render function that wraps all child components our state and dispatch context providers
export const AppProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    if (!window.kervFrame) {
      // Only attempt to load SuperFrame resources if URLs are provided
      if (config.superFrame && config.superFrameCSS) {
        Promise.all([
          loadExternalResources(config.superFrame),
          loadExternalResources(config.superFrameCSS),
        ]).then(() => {
          setTimeout(() => {
            dispatch(setSuperFrameLoaded(true));
          }, 1000)
        })
          .catch((e) => {
            console.error(`SuperFrame script could not be loaded:`, e);
            dispatch(setError({
              message: `Required script failed to load: ${config.superFrame}`,
              status: 500
            }));
          });
      } else {
        // If SuperFrame URLs are not provided, mark as loaded anyway to not block the app
        console.warn('SuperFrame URLs not provided. Continuing without them.');
        dispatch(setSuperFrameLoaded(true));
      }
    } else {
      dispatch(setSuperFrameLoaded(true));
    }
  }, []);

  // For development - handle API failures with mock data
  useEffect(() => {
    if (config.hostEnv === 'development' && state.error?.status === 404) {
      console.warn('API endpoint not found, using mock data for development');
      // Provide mock data for development
      dispatch(setSuperFrameLoaded(true));
      
      // Clear the error so the app can proceed
      dispatch(setError(null));
    }
  }, [state.error]);

  return (
    <AppContext.Provider value={state}>
      <AppDispatch.Provider value={dispatch}>
        {children}
      </AppDispatch.Provider>
    </AppContext.Provider>
  );
};

// provides access to any component needing to access state
export const useAppState = (): AppContextType => {
  const context = React.useContext(AppContext) as AppContextType;
  if (context === undefined) {
    throw new Error('Please use within AppProvider');
  }
  return context;
};

// provides access to any component needing to dispatch state
// we need to generalize the dispatch creative action type
export const useAppDispatch = (): React.Dispatch<DispatchType> => {
  const context = React.useContext(AppDispatch);
  if (context === undefined) {
    throw new Error('Please use within AppProvider');
  }
  return context;
};
